import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authFetch } from '../features/auth/auth-fetch.js';
import { useAuth } from '../features/auth/use-auth.js';
import { useAuthStore, type SessionUser } from '../features/auth/auth.store.js';
import { Field, FormActions } from '../components/ui/form.js';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { SHORTCUTS, type ShortcutEntry } from '../features/shortcuts/registry.js';
import { getMySettings, updateMySettings, type UserSettings } from '../features/preferences/api.js';
import { DIGEST_EMAIL_KINDS } from '@cp/schemas';

const ALL_DIGEST_KINDS: string[] = DIGEST_EMAIL_KINDS.map((k) => k.kind);

const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'H' },
  { value: 2, label: 'K' },
  { value: 3, label: 'Sz' },
  { value: 4, label: 'Cs' },
  { value: 5, label: 'P' },
  { value: 6, label: 'Szo' },
  { value: 7, label: 'V' },
];

interface UpdateBody {
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}

async function updateMe(body: UpdateBody): Promise<SessionUser> {
  const res = await authFetch('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let title = 'Nem sikerült menteni a profilt';
    try {
      const j = (await res.json()) as { title?: string };
      if (j.title) title = j.title;
    } catch {
      /* ignore */
    }
    throw new Error(title);
  }
  return res.json();
}

function ProfilePage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setEmail(user.email);
    setDirty(false);
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      updateMe({
        firstName: firstName || null,
        lastName: lastName || null,
        email,
      }),
    onSuccess: (fresh) => {
      // Push the freshly-saved user into the global auth store so the
      // dashboard greeting and sidebar avatar update immediately.
      const token = (useAuthStore.getState() as unknown as { accessToken?: string }).accessToken;
      useAuthStore.setState({ user: fresh });
      void qc.invalidateQueries();
      setDirty(false);
      // Token reference kept for future expansion (re-issue on email change).
      void token;
    },
  });

  function track<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setDirty(true);
    };
  }

  if (!user) return <p className="text-body text-[var(--fg-2)]">Betöltés…</p>;

  const initials =
    `${(firstName[0] ?? '').toUpperCase()}${(lastName[0] ?? '').toUpperCase()}` ||
    (user.email[0] ?? '?').toUpperCase();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <div className="h2-eyebrow">Fiók</div>
        <h1 className="mt-1.5 font-sans text-h1 font-normal leading-none tracking-[-0.02em] text-[var(--ink-700)]">
          Profilom
        </h1>
        <p className="mt-2 text-body text-[var(--fg-2)]">
          A neved megjelenik a dashboard köszöntésben és a folyamatok naplójában. Az email cím
          egyben a bejelentkezési azonosítód is.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-6 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand-500)] text-h2 font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="text-body font-semibold text-[var(--ink-700)]">
              {firstName || lastName ? `${lastName} ${firstName}`.trim() : user.email}
            </div>
            <div className="text-meta text-[var(--fg-faint)]">
              {user.role === 'admin'
                ? 'Adminisztrátor'
                : user.role === 'editor'
                  ? 'Szerkesztő'
                  : 'Megtekintő'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Vezetéknév" htmlFor="last-name">
            <Input
              id="last-name"
              value={lastName}
              placeholder="Pl. Kovács"
              onChange={(e) => track(setLastName)(e.target.value)}
            />
          </Field>
          <Field label="Keresztnév" htmlFor="first-name" hint="Ezzel köszön a dashboard">
            <Input
              id="first-name"
              value={firstName}
              placeholder="Pl. Anna"
              onChange={(e) => track(setFirstName)(e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Email cím"
          htmlFor="email"
          hint="A bejelentkezési azonosítód. Csere után új belépéssel érvényes."
        >
          <Input
            id="email"
            type="email"
            value={email}
            required
            onChange={(e) => track(setEmail)(e.target.value)}
          />
        </Field>

        {save.isError && (
          <p className="text-body text-[var(--danger)]">Hiba: {(save.error as Error).message}</p>
        )}

        <FormActions>
          {dirty && (
            <span className="mr-2 text-meta text-[var(--fg-2)]">
              Nem mentett változások vannak.
            </span>
          )}
          <Button type="submit" disabled={save.isPending || !dirty}>
            {save.isPending ? 'Mentés…' : 'Mentés'}
          </Button>
        </FormActions>
      </form>

      <SettingsPanel />
      <ShortcutsPanel />
    </div>
  );
}

function SettingsPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['my-settings'], queryFn: getMySettings });
  const settings = q.data ?? {};
  const save = useMutation({
    mutationFn: (patch: Partial<UserSettings>) => updateMySettings(patch),
    onSuccess: (next) => {
      qc.setQueryData(['my-settings'], next);
      // The theme handler in app.tsx reads from localStorage on boot; mirror
      // the choice there so a page refresh keeps it, and update the DOM
      // attribute now so the change is immediately visible.
      if (next.theme && typeof window !== 'undefined') {
        window.localStorage.setItem('hestia.theme', next.theme);
        const root = document.documentElement;
        if (next.theme === 'dark') root.setAttribute('data-theme', 'dark');
        else if (next.theme === 'light') root.removeAttribute('data-theme');
        else {
          const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (systemDark) root.setAttribute('data-theme', 'dark');
          else root.removeAttribute('data-theme');
        }
      }
    },
  });

  const theme = settings.theme ?? 'system';
  const landingPage = settings.landingPage ?? 'dashboard';
  // Undefined = never configured → everything is in the digest by default, so
  // reflect that by pre-checking all boxes. The first toggle materialises the
  // selection into an explicit array.
  const emailKinds = new Set(settings.emailNotifications ?? ALL_DIGEST_KINDS);
  const digestHour = settings.digestHour ?? 7;
  const quietDays = new Set(settings.quietDays ?? []);
  // Checkbox represents "warnings ON": only an explicit false disables them.
  const aiRegenerateWarnings = settings.aiRegenerateWarnings !== false;

  function toggleEmailKind(kind: string) {
    const next = new Set(emailKinds);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    save.mutate({ emailNotifications: Array.from(next) });
  }
  function toggleQuietDay(day: number) {
    const next = new Set(quietDays);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    save.mutate({ quietDays: Array.from(next).sort() });
  }

  const groupClass = 'border-t border-[var(--app-divider)] pt-5 first:border-t-0 first:pt-0';
  const eyebrowClass =
    'mb-3 text-meta font-semibold uppercase tracking-wide text-[var(--fg-faint)]';

  return (
    <section className="rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
      <header className="mb-5">
        <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Beállítások</h2>
        <p className="mt-1 text-meta text-[var(--fg-2)]">
          Személyre szabhatod a felület alapértelmezett viselkedését. A változások azonnal
          mentődnek.
        </p>
      </header>

      <div className="space-y-5">
        {/* --- Megjelenés --- */}
        <div className={groupClass}>
          <div className={eyebrowClass}>Megjelenés</div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Téma" htmlFor="theme">
              <select
                id="theme"
                value={theme}
                onChange={(e) => save.mutate({ theme: e.target.value as UserSettings['theme'] })}
                className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)]"
              >
                <option value="system">Rendszer szerint</option>
                <option value="light">Világos</option>
                <option value="dark">Sötét</option>
              </select>
            </Field>
            <Field label="Bejelentkezés utáni kezdőoldal" htmlFor="landing">
              <select
                id="landing"
                value={landingPage}
                onChange={(e) =>
                  save.mutate({ landingPage: e.target.value as UserSettings['landingPage'] })
                }
                className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)]"
              >
                <option value="dashboard">Dashboard</option>
                <option value="plans">Tervek</option>
                <option value="hotels">Hotelek</option>
                <option value="inbox">Értesítések</option>
              </select>
            </Field>
          </div>
        </div>

        {/* --- Napi összefoglaló tartalma --- */}
        <div className={groupClass}>
          <div className={eyebrowClass}>Napi összefoglaló tartalma</div>
          <p className="-mt-1.5 mb-2 text-meta text-[var(--fg-2)]">
            A kipipált típusok bekerülnek a napi összefoglaló e-mailbe, az alább beállított
            időpontban. A többi csak a haranggomb listájában jelenik meg. Alapból minden típus benne
            van.
          </p>
          <div className="space-y-1.5">
            {DIGEST_EMAIL_KINDS.map((k) => (
              <label
                key={k.kind}
                className="flex items-center gap-2 text-body text-[var(--ink-700)]"
              >
                <input
                  type="checkbox"
                  checked={emailKinds.has(k.kind)}
                  onChange={() => toggleEmailKind(k.kind)}
                />
                {k.label}
              </label>
            ))}
          </div>
        </div>

        {/* --- Napi összefoglaló ütemezése --- */}
        <div className={groupClass}>
          <div className={eyebrowClass}>Napi összefoglaló ütemezése</div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Napi összefoglaló időpontja" htmlFor="digest-hour">
              <select
                id="digest-hour"
                value={digestHour}
                onChange={(e) => save.mutate({ digestHour: Number(e.target.value) })}
                className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)]"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </Field>
            <div>
              <div className="mb-0.5 text-meta font-medium text-[var(--ink-700)]">
                Csendes napok
              </div>
              <p className="mb-1.5 text-meta text-[var(--fg-2)]">
                A kiemelt (bejelölt) napokon nem küldünk összefoglaló e-mailt — ezek a csendes
                napok.
              </p>
              <div className="flex gap-1.5">
                {WEEKDAYS.map((d) => {
                  const on = quietDays.has(d.value);
                  return (
                    <button
                      type="button"
                      key={d.value}
                      onClick={() => toggleQuietDay(d.value)}
                      className={`h-8 w-8 rounded-full border text-meta font-medium transition-colors ${
                        on
                          ? 'border-[var(--brand-500)] bg-[var(--brand-500)] text-white'
                          : 'border-[var(--app-divider)] text-[var(--ink-700)] hover:border-[var(--brand-400)]'
                      }`}
                      title={`${d.label} — ${on ? 'csendes nap (nincs e-mail)' : 'kap összefoglalót'}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* --- AI figyelmeztetések --- */}
        <div className={groupClass}>
          <div className={eyebrowClass}>AI figyelmeztetések</div>
          <label className="flex items-center gap-2 text-body text-[var(--ink-700)]">
            <input
              type="checkbox"
              checked={aiRegenerateWarnings}
              onChange={(e) => save.mutate({ aiRegenerateWarnings: e.target.checked })}
            />
            AI újragenerálás figyelmeztetés
          </label>
          <p className="mt-0.5 text-meta text-[var(--fg-2)]">
            Rákérdez, ha ugyanazt a posztot rövid időn belül többször újragenerálod.
          </p>
        </div>
      </div>

      {save.isError && (
        <p className="mt-4 text-body text-[var(--danger)]">
          Mentés hiba: {(save.error as Error).message}
        </p>
      )}
    </section>
  );
}

function ShortcutsPanel() {
  const grouped = useMemo(() => {
    const m = new Map<ShortcutEntry['group'], ShortcutEntry[]>();
    for (const s of SHORTCUTS) {
      const arr = m.get(s.group) ?? [];
      arr.push(s);
      m.set(s.group, arr);
    }
    return Array.from(m.entries());
  }, []);

  return (
    <section className="space-y-4 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
      <header>
        <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Billentyűparancsok</h2>
        <p className="mt-1 text-meta text-[var(--fg-2)]">
          A leggyakoribb műveletekhez tartozó gyorsbillentyűk. Bárhol az alkalmazásban használhatók
          (kivéve szövegmezőben).
        </p>
      </header>

      <div className="space-y-4">
        {grouped.map(([group, entries]) => (
          <div key={group}>
            <div className="mb-1.5 text-meta font-semibold uppercase tracking-wide text-[var(--fg-faint)]">
              {group}
            </div>
            <ul className="divide-y divide-[var(--app-divider)] rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)]">
              {entries.map((s) => (
                <li
                  key={`${group}-${s.keys}`}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span className="text-body text-[var(--ink-700)]">{s.label}</span>
                  <kbd className="rounded border border-[var(--app-divider)] bg-[var(--app-surface)] px-1.5 py-0.5 font-mono text-meta text-[var(--ink-700)]">
                    {s.keys}
                  </kbd>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute('/app/profile')({
  component: ProfilePage,
});
